import { DynamicUGOHub } from './DynamicUGOHub';
import { DynamicTableStoreBase } from '../DynamicTableStoreBase';
import { ProfileKeyCode } from '../DynamicProfileEnums';
import { XyzPoint } from 'cd-eft/models/SurveyEngineering/XyzPoint';
import stores from '../..';
import { formatStationString, parseStationString } from 'cd-eft/utils/stationUtilities';
import { ProfileController } from 'cd-eft/services/eftSetApi';
import { IUgoVertexParamsDTO } from '../../UGODesignStore';

const { ugoDesignStore, designStore } = stores.AppStore;
class DynamicUGOTableStore extends DynamicTableStoreBase {
  protected hub: DynamicUGOHub;
  private _initialUgoDesignStation;
  /**
   *
   */
  constructor(hub: DynamicUGOHub) {
    super();

    this.hub = hub;
    this.profileKeys = [ProfileKeyCode.TGS, ProfileKeyCode.REACH];
  }

  // adapt to global stores
  set setStore(setStore) {
    this.hub.setStore = setStore;
  }

  get setStore() {
    return this.hub.setStore;
  }

  public set ugoDesignStore(store) {
    this.hub.ugoDesignStore = store;
  }

  public get ugoDesignStore() {
    return this.hub.ugoDesignStore;
  }

  private get initialUgoDesignStation() {
    if (!this._initialUgoDesignStation) {
      this._initialUgoDesignStation = ugoDesignStore.initialUgoDesignStation;
    }
    return this._initialUgoDesignStation;
  }

  /** abstract method implementation */
  protected insertRow(newVertex: any, vertexIndex: any) { }
  protected addRow(vertex: XyzPoint, state: string, vertexIndex: number) { }
  protected calculateStationDistances(indexToDelete?: number) { }
  /** end of abstract method implementation */

  protected reset() {
    ugoDesignStore.ugoDesignObject.ugoDesignStations.length = 0;
  }

  public async addRows(channelData: IUgoVertexParamsDTO[]) {
    designStore.setLoader('Loading profile reaches...');

    const stations = [{ ...this.initialUgoDesignStation }];
    channelData.forEach((point, idx) => {
      const newRow = { ...this.initialUgoDesignStation, ...point } as IUgoVertexParamsDTO;
      // Format numeric values
      if (newRow.pipeFlowElev !== null && newRow.pipeFlowElev !== undefined) {
        newRow.pipeFlowElev = Number(newRow.pipeFlowElev.toFixed(1));
      }
      newRow.status = point.status ?? 'IN PROGRESS';

      const prevStation = channelData[idx - 1];
      if (prevStation) {
        const deltaCurY = Number(prevStation.pipeFlowElev.toFixed(1)) - newRow.pipeFlowElev;
        const deltaCurX =
          parseStationString(prevStation.endStation?.toString() ?? '0') -
          parseStationString(newRow.endStation?.toString() ?? '0');
        newRow.grade = deltaCurY / deltaCurX;
        newRow.stationDist = Math.abs(deltaCurX);
      }

      stations.push(newRow);
    });
    stations.splice(0, 1);
    ugoDesignStore.ugoDesignObject.ugoDesignStations = stations;

    await this.hub.ProfileStore?.createChannelFromSavedUgo();

    // Clone object after digitizing reaches
    ugoDesignStore.ugoGroupsOriginals = [];
    ugoDesignStore.ugoGroupsOriginals.push(
      {
        groupCount: 0,
        currentObject: JSON.parse(JSON.stringify(ugoDesignStore.ugoDesignObject))
      }
    );
    designStore.setLoader('');
  }

  protected async insertRows(dataPoint: XyzPoint, vertexIndex: number, keyCode: ProfileKeyCode, addUgoEdits?: any) {
    const stations1 = ugoDesignStore.ugoDesignObject.ugoDesignStations;
    const stations = JSON.parse(JSON.stringify(stations1));

    const originalStations = JSON.parse(JSON.stringify(stations));

    // Find the index of 'P-0' in the stations array
    const r0Index = stations.findIndex(station => station.reachId === 'P-0');

    // Adjust the vertexIndex by the index of 'P-0'
    vertexIndex += r0Index;
    stations[vertexIndex].status = "IN PROGRESS";
    if (vertexIndex < 0 || vertexIndex >= stations.length) {
      return;
    }

    const formattedStationX = formatStationString(dataPoint.getX().toFixed(1));
    const formattedStationY = dataPoint.getY().toFixed(1);

    // Calculate groundZ for current reach
    const payload = {
      alignedElementDesignParametersId: ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId,
      reachStationDistance: parseFloat(dataPoint.getX().toFixed(1)) // new station x value
    };

    const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST(payload);

    let groundZ = 0;
    if (response.data && response.data.groundElevation !== undefined) {
      groundZ = parseFloat(response.data.groundElevation.toFixed(1));
    }

    const newRow = {
      ...stations[vertexIndex - 1],
      endStation: formattedStationX,
      pipeFlowElev: parseFloat(formattedStationY),
      groundElev: groundZ,
      status: 'IN PROGRESS',
      reachId: `P-${vertexIndex - r0Index}`,
      startStation: stations[vertexIndex - 1].endStation,
    };

    // Calculate stationLength and grade for the new row
    const prevStation = stations[vertexIndex - 1];
    if (prevStation.reachId !== "START") {
      const deltaCurY = (prevStation.pipeFlowElev) - newRow.pipeFlowElev;
      const deltaCurX = parseStationString(prevStation.endStation) - parseStationString(newRow.endStation);
      if (deltaCurX !== 0) {
        newRow.grade = deltaCurY / deltaCurX;
        newRow.stationDist = Math.abs(deltaCurX);
      }
    }

    const stationsInsertedAndUpdated: any = [];
    stations.splice(vertexIndex, 0, newRow);
    stationsInsertedAndUpdated.push(newRow);

    // Update reachId, startStation, groundZ, cutFill, stationLength, and grade for the rows after the inserted row
    for (let index = vertexIndex + 1; index < stations.length; index++) {
      if (stations[index].reachId !== "END" && stations[index].reachId !== "START" && stations[index].reachId !== "P-0") {
        // Update Status
        stations[index].status = 'IN PROGRESS'
        const reachIdNumber = parseInt(stations[index].reachId.split('-')[1]);
        stations[index].reachId = `P-${reachIdNumber + 1}`;
        stations[index].startStation = stations[index - 1].endStation;

        if (index === vertexIndex + 1 || index === stations.length - 1) {
          // Recalculate groundZ and cutFill
          const payload = {
            alignedElementDesignParametersId: ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId,
            reachStationDistance: parseFloat(stations[index].endStation) // station x value of the current row
          };

          const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST(payload);

          if (response.data && response.data.groundElevation !== undefined) {
            stations[index].groundElev = parseFloat(response.data.groundElevation.toFixed(1));
          }

          // Recalculate stationLength and grade
          const prevStation = stations[index - 1];
          if (prevStation.reachId !== "START") {
            const deltaCurY = prevStation.pipeFlowElev - stations[index].pipeFlowElev;
            const deltaCurX = parseStationString(prevStation.endStation) - parseStationString(stations[index].endStation);
            if (deltaCurX !== 0) {
              stations[index].grade = deltaCurY / deltaCurX;
              stations[index].stationDist = Math.abs(deltaCurX);
            }
          }
        }
      }
      // Only push the station into the array if its reachId is not "END"
      if (stations[index].reachId !== "END") {
        stationsInsertedAndUpdated.push(stations[index]); // Push each updated station into the array
      }
    }
    const allUpdatedValues = new Map();

    // Compare original and updated stations to find the stations that were added or modified
    stationsInsertedAndUpdated.forEach((updatedStation) => {
      const originalStation = originalStations.find((os) => {
        if (updatedStation.originalReachId) {
          return os.reachId === updatedStation.originalReachId;
        }
        return os.endStation === updatedStation.endStation;
      });

      if (!originalStation) {
        // A newly added station
        allUpdatedValues.set("Add", updatedStation);
      } else {
        const isChanged =
          originalStation.endStation !== updatedStation.endStation ||
          originalStation.reachId !== updatedStation.reachId ||
          parseFloat(originalStation.pipeFlowElev) !== parseFloat(updatedStation.pipeFlowElev);

        if (isChanged) {
          allUpdatedValues.set(originalStation.reachId, updatedStation);
        }
      }
    });
    ugoDesignStore.ugoDesignObject.ugoDesignStations = stations;
    await addUgoEdits(allUpdatedValues, true);
    await this.hub.ProfileStore?.createChannelFromSavedUgo();
  }

  public async modifyRows(dataPoint: XyzPoint, vertexIndex: number, addUgoEdits?: any): Promise<Map<any, any> | void> {
    const stations = ugoDesignStore.ugoDesignObject.ugoDesignStations;

    if (!stations || stations.length < 1) {
      return;
    }
    const originalStations = JSON.parse(JSON.stringify(stations));

    //update table values based on changed station values
    await this.hub.ProfileStore?.updateUgoTableOnChangeStationValue(dataPoint, vertexIndex);

    // Prepare the updated values
    const updatedStations = [stations[vertexIndex]];
    if (vertexIndex + 1 < stations.length && stations[vertexIndex + 1].reachId !== 'END') {
      updatedStations.push(stations[vertexIndex + 1]);
    }

    // Create a map of updated values
    const allUpdatedValues = new Map();
    updatedStations.forEach((updatedStation) => {
      const originalStation = originalStations.find(
        (os) => os.reachId === updatedStation.reachId
      );
      if (originalStation) {
        // Check if the station has been updated
        if (
          originalStation.endStation !== updatedStation.endStation ||
          originalStation.pipeFlowElev !== updatedStation.pipeFlowElev ||
          originalStation.grade !== updatedStation.grade ||
          originalStation.stationDist !== updatedStation.stationDist ||
          originalStation.groundElev !== updatedStation.groundElev
        ) {
          allUpdatedValues.set(updatedStation.reachId, updatedStation);
        }
      } else {
        // New station added
        allUpdatedValues.set(updatedStation.reachId, updatedStation);
      }
    });
    ugoDesignStore.ugoDesignObject.ugoDesignStations = stations;
    await addUgoEdits(allUpdatedValues, true);
    await this.hub.ProfileStore?.redrawUgoProfiles();
  }

}

export { DynamicUGOTableStore };
